import { Injectable } from '@angular/core';

/**
 * Declarative, designer-driven aggregation for widget data.
 *
 * A widget receives raw rows from the API and a config block (typically under
 * `item.config.aggregation`) describing how to reduce those rows into a single
 * summary row and/or grouped rows. Supports sum / avg / min / max / count /
 * first / last per field, plus derived fields (percent, ratio, diff) computed
 * on top of the aggregated values, plus optional time-series extraction for
 * sparklines and trend arrows.
 */

export type AggregationOp = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last';

export interface FieldRule {
  /** Raw column name in the source rows. */
  source: string;
  /** How to reduce values of `source` across rows. */
  op: AggregationOp;
  /**
   * 'group' (default) – aggregate within this group's rows only.
   * 'all'   – aggregate across ALL input rows (grand total), injected into
   *           every group row so computed rules can use it as a denominator.
   */
  scope?: 'group' | 'all';
}

export type ComputedOp = 'ratio' | 'percent' | 'diff' | 'sum' | 'expr';

export interface ComputedRule {
  op: ComputedOp;
  /** Name of an already-aggregated field. */
  numerator?: string;
  /** Name of an already-aggregated field (for ratio/percent/diff). */
  denominator?: string;
  /** Additional operands for multi-term ops like `sum`. */
  operands?: string[];
  /** Multiplier applied after the base computation (e.g. 100 for percent). */
  scale?: number;
  /** Decimal places for the final value. */
  round?: number;
  /** String suffix appended to the output (e.g. '%'). Produces a string value. */
  suffix?: string;
}

export interface TimeSeriesOptions {
  /** Field to sort groups by. Defaults to the aggregation's `groupBy`. */
  orderBy?: string;
  /** Keep only the last N groups after sorting. */
  limit?: number;
  /** How to format group keys for chart labels. Use 'custom' with `dateFormat`. */
  labelFormat?: 'month' | 'year' | 'quarter' | 'weekday' | 'monthYear' | 'shortDate' | 'isoDate' | 'time' | 'dateTime' | 'date' | 'raw' | 'custom';
  /**
   * Custom date format string used when `labelFormat` is `'custom'`.
   * Supported tokens: YYYY, YY, MMMM, MMM, MM, M, DD, D, HH, mm, ss, A/a.
   * Example: 'DD MMM YYYY' → '16 Jan 2026'
   */
  dateFormat?: string;
  /** Which aggregated or computed field to pull values from for the sparkline. */
  trackField?: string;
}

export interface AggregationConfig {
  /**
   * Field(s) to group by. Pass a string array for composite (multi-field) keys.
   * Omit for a single summary row.
   */
  groupBy?: string | string[];
  /** Per-field aggregation rules. Key is the output field name. */
  fields: { [outputField: string]: FieldRule };
  /** Derived fields computed after aggregation. Key is the output field name. */
  computed?: { [outputField: string]: ComputedRule };
  /** Optional time-series / sparkline extraction. */
  timeSeries?: TimeSeriesOptions;
}

export type Row = { [key: string]: any };

export interface AggregationResult {
  /** A single row containing the aggregation of all input rows. */
  summary: Row;
  /** One row per group when `groupBy` is set, sorted by group key ascending. */
  groups: Row[];
  /** The group key for each entry in `groups` (raw, unformatted). */
  groupKeys: string[];
}

export interface TimeSeriesResult {
  values: number[];
  labels: string[];
}

export interface TrendResult {
  direction: 'UP' | 'DOWN' | 'FLAT';
  value: string | null|any;
}

@Injectable({ providedIn: 'root' })
export class DataAggregationService {
  /**
   * Reduce raw rows into a summary row and (optionally) per-group rows using
   * the declarative config.
   */
  aggregate(rows: any[], cfg: AggregationConfig): AggregationResult {
    const safeRows = Array.isArray(rows) ? rows : [];
    const groupKeys: string[] = [];
    const groups: Row[] = [];

    // Split fields into per-group vs grand-total (scope:'all') buckets
    const groupFields: { [k: string]: FieldRule } = {};
    const allFields:   { [k: string]: FieldRule } = {};
    for (const [k, rule] of Object.entries(cfg.fields || {})) {
      if (rule.scope === 'all') allFields[k] = rule;
      else groupFields[k] = rule;
    }
    // Pre-compute grand totals once; injected into every group row so computed
    // rules (e.g. pct = group_count / grand_total) can reference them
    const grandTotals = Object.keys(allFields).length
      ? this.reduceFields(safeRows, allFields)
      : {};

    if (cfg.groupBy) {
      const groupByArr = Array.isArray(cfg.groupBy) ? cfg.groupBy : [cfg.groupBy];
      const buckets = new Map<string, any[]>();
      for (const r of safeRows) {
        const k = groupByArr.map(f => String(r?.[f] ?? 'unknown')).join('|');
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k)!.push(r);
      }
      const sortedKeys = Array.from(buckets.keys()).sort();
      for (const k of sortedKeys) {
        const groupRows = buckets.get(k)!;
        const row = this.reduceFields(groupRows, groupFields);
        // Inject grand-total values so computed denominators work across groups
        Object.assign(row, grandTotals);
        // Stamp each groupBy field onto the row
        for (const f of groupByArr) {
          row[f] = this.castGroupKey(String(groupRows[0]?.[f] ?? 'unknown'), groupRows[0]?.[f]);
        }
        this.applyComputed(row, cfg.computed);
        groupKeys.push(k);
        groups.push(row);
      }
    }

    const summary = this.reduceFields(safeRows, cfg.fields);
    this.applyComputed(summary, cfg.computed);

    return { summary, groups, groupKeys };
  }

  /**
   * Extract a numeric series and matching labels from grouped results for
   * sparkline / trend consumption.
   */
  buildTimeSeries(result: AggregationResult, opts?: TimeSeriesOptions): TimeSeriesResult {
    if (!opts || !result.groups.length) {
      return { values: [], labels: [] };
    }

    let groups = result.groups;
    let keys = result.groupKeys;

    // Optional re-sort on a different field
    if (opts.orderBy && groups.length > 1) {
      const idx = groups
        .map((_, i) => i)
        .sort((a, b) => {
          const va = groups[a]?.[opts.orderBy!];
          const vb = groups[b]?.[opts.orderBy!];
          return va < vb ? -1 : va > vb ? 1 : 0;
        });
      groups = idx.map(i => groups[i]);
      keys = idx.map(i => keys[i]);
    }

    if (opts.limit && opts.limit > 0 && groups.length > opts.limit) {
      groups = groups.slice(-opts.limit);
      keys = keys.slice(-opts.limit);
    }

    const track = opts.trackField;
    const values = track
      ? groups.map(g => this.toNumber(g[track]))
      : [];
    const labels = keys.map(k => this.formatLabel(k, opts.labelFormat, opts.dateFormat));
    return { values, labels };
  }

  /**
   * Compute a simple trend descriptor by comparing the last value to the
   * previous value in a series.
   */
  computeTrend(values: number[]): TrendResult {
    if (!values || values.length < 2) return { direction: 'FLAT', value: null };
    const latest = values[values.length - 1];
    const previous = values[values.length - 2];
    const diff = latest - previous;
    if (diff === 0) return { direction: 'FLAT', value: '0' };
    const direction: 'UP' | 'DOWN' = diff > 0 ? 'UP' : 'DOWN';
    const absDiff = Math.abs(diff);
    const formatted:any = absDiff >= 1000 ? `${(absDiff / 1000).toFixed(1)}k` : String(absDiff.toFixed(2));
    const sign = diff > 0 ? '+' : '-';
    return { direction, value: `${sign}${formatted}` };
  }

  // ─── internals ────────────────────────────────────────────────────────────

  private reduceFields(rows: any[], fields: { [k: string]: FieldRule }): Row {
    const out: Row = {};
    if (!fields) return out;

    for (const outKey of Object.keys(fields)) {
      const rule = fields[outKey];
      const src = rule.source;
      let val: number = 0;

      switch (rule.op) {
        case 'sum':
          val = rows.reduce((a, r) => a + this.toNumber(r?.[src]), 0);
          break;
        case 'avg': {
          if (!rows.length) { val = 0; break; }
          const total = rows.reduce((a, r) => a + this.toNumber(r?.[src]), 0);
          val = total / rows.length;
          break;
        }
        case 'min':
          val = rows.length
            ? rows.reduce((a, r) => Math.min(a, this.toNumber(r?.[src])), Number.POSITIVE_INFINITY)
            : 0;
          if (val === Number.POSITIVE_INFINITY) val = 0;
          break;
        case 'max':
          val = rows.length
            ? rows.reduce((a, r) => Math.max(a, this.toNumber(r?.[src])), Number.NEGATIVE_INFINITY)
            : 0;
          if (val === Number.NEGATIVE_INFINITY) val = 0;
          break;
        case 'count':
          val = rows.reduce((a, r) => a + (r?.[src] != null && r[src] !== '' ? 1 : 0), 0);
          break;
        case 'first':
          out[outKey] = rows.length ? rows[0]?.[src] : null;
          continue;
        case 'last':
          out[outKey] = rows.length ? rows[rows.length - 1]?.[src] : null;
          continue;
      }

      out[outKey] = val;
    }
    return out;
  }

  private applyComputed(row: Row, computed?: { [k: string]: ComputedRule }): void {
    if (!computed) return;
    for (const outKey of Object.keys(computed)) {
      const rule = computed[outKey];
      let val: number | null = null;

      switch (rule.op) {
        case 'ratio':
        case 'percent': {
          const num = this.toNumber(row[rule.numerator || '']);
          const den = this.toNumber(row[rule.denominator || '']);
          val = den !== 0 ? num / den : 0;
          if (rule.op === 'percent') val = val * 100;
          break;
        }
        case 'diff': {
          const num = this.toNumber(row[rule.numerator || '']);
          const den = this.toNumber(row[rule.denominator || '']);
          val = num - den;
          break;
        }
        case 'sum': {
          const operands = rule.operands || [];
          val = operands.reduce((a, k) => a + this.toNumber(row[k]), 0);
          break;
        }
        case 'expr':
          val = 0;
          break;
      }

      if (val == null) continue;

      if (typeof rule.scale === 'number') val = val * rule.scale;
      if (typeof rule.round === 'number') {
        const factor = Math.pow(10, rule.round);
        val = Math.round(val * factor) / factor;
      } else {
        val = Math.round(val);
      }

      row[outKey] = rule.suffix ? `${val}${rule.suffix}` : val;
    }
  }

  private toNumber(v: any): number {
    if (v == null) return 0;
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    const n = Number(String(v).replace(/[^0-9.\-]/g, ''));
    return isFinite(n) ? n : 0;
  }

  /** Preserve the original type of the group key when possible. */
  private castGroupKey(stringKey: string, original: any): any {
    return original !== undefined ? original : stringKey;
  }

  private formatLabel(key: string, fmt?: TimeSeriesOptions['labelFormat'], dateFormat?: string): string {
    if (!fmt || fmt === 'raw') return key;
    const d = new Date(key);
    if (isNaN(d.getTime())) return key;

    switch (fmt) {
      case 'month':
        return d.toLocaleDateString('en-US', { month: 'short' });
      case 'year':
        return d.toLocaleDateString('en-US', { year: 'numeric' });
      case 'quarter':
        return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
      case 'weekday':
        return d.toLocaleDateString('en-US', { weekday: 'short' });
      case 'monthYear':
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      case 'shortDate':
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'isoDate':
        return d.toISOString().slice(0, 10);
      case 'time':
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      case 'dateTime':
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' } as any);
      case 'date':
        return d.toLocaleDateString('en-US');
      case 'custom':
        return dateFormat ? this.applyDateFormat(d, dateFormat) : key;
      default:
        return key;
    }
  }

  /**
   * Replace tokens in a custom date format string.
   * Supported tokens: YYYY, YY, MMMM, MMM, MM, M, DD, D, HH, hh, mm, ss, A, a
   * Example: 'DD MMM YYYY' → '16 Jan 2026'
   */
  private applyDateFormat(d: Date, format: string): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const hours24 = d.getHours();
    const hours12 = hours24 % 12 || 12;
    const pad = (n: number) => n < 10 ? '0' + n : String(n);

    const tokens: { [token: string]: string } = {
      'YYYY': String(d.getFullYear()),
      'YY': String(d.getFullYear()).slice(-2),
      'MMMM': months[d.getMonth()],
      'MMM': monthsShort[d.getMonth()],
      'MM': pad(d.getMonth() + 1),
      'M': String(d.getMonth() + 1),
      'DD': pad(d.getDate()),
      'D': String(d.getDate()),
      'HH': pad(hours24),
      'hh': pad(hours12),
      'mm': pad(d.getMinutes()),
      'ss': pad(d.getSeconds()),
      'A': hours24 < 12 ? 'AM' : 'PM',
      'a': hours24 < 12 ? 'am' : 'pm',
    };

    // Replace longest tokens first to avoid partial matches (e.g. MMMM before MMM before MM before M)
    return Object.keys(tokens)
      .sort((a, b) => b.length - a.length)
      .reduce((result, token) => result.replace(new RegExp(token, 'g'), tokens[token]), format);
  }
}
